<script setup lang="ts">
  import { ChevronDownIcon } from '@heroicons/vue/solid';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';

  const props = defineProps<{
    label: string;
    allocation: number | string;
    tds: number | string;
    payable: number | string;
    payments: number | string;
    viaAdvances: number | string;
    paid: number | string;
    balance: number | string;
    bold?: boolean;
    collapsible?: boolean;
    expanded?: boolean;
  }>();

  const emit = defineEmits(['toggle']);

  const cellText = (value: number | string) =>
    value === '-' || value === undefined ? '-' : formatToIndianCurrency(+value);
  const isNegative = (value: number | string) => value !== '-' && value !== undefined && +value < 0;
  const isDash = (value: number | string) => value === '-' || value === undefined;
  const isZero = (value: number | string) => !isDash(value) && !isNegative(value) && +value === 0;

  const cellClass = (value: number | string) => {
    if (isNegative(value)) return 'text-red-700';
    if (isDash(value)) return 'text-gray-400';
    if (isZero(value)) return 'text-gray-500';
    return 'text-gray-900';
  };
</script>

<template>
  <tr :class="props.bold ? 'bg-gray-200' : 'bg-gray-100/80'">
    <td
      :class="`border border-gray-300 text-gray-800 text-[13px] px-3 py-2.5 font-semibold ${props.bold ? 'border-t-2 border-t-gray-400' : ''}`"
    >
      <button
        v-if="props.collapsible"
        type="button"
        class="flex items-center gap-1.5 font-semibold text-gray-800 !text-[13px] hover:text-blue-700 transition-colors"
        @click="emit('toggle')"
      >
        <ChevronDownIcon
          class="w-4 h-4 text-gray-400 transition-transform"
          :class="{ '-rotate-90': !props.expanded }"
        />
        {{ props.label }}
      </button>
      <span v-else>{{ props.label }}</span>
    </td>
    <td
      :class="`border border-gray-300 text-sm px-3 py-2.5 text-right ${props.bold ? 'font-semibold border-t-2 border-t-gray-400' : ''} ${cellClass(props.allocation)}`"
    >
      {{ cellText(props.allocation) }}
    </td>
    <td
      :class="`border border-gray-300 text-sm px-3 py-2.5 text-right ${props.bold ? 'font-semibold border-t-2 border-t-gray-400' : ''} ${cellClass(props.tds)}`"
    >
      {{ cellText(props.tds) }}
    </td>
    <td
      :class="`border border-gray-300 text-sm px-3 py-2.5 text-right ${props.bold ? 'font-semibold border-t-2 border-t-gray-400' : ''} ${cellClass(props.payable)}`"
    >
      {{ cellText(props.payable) }}
    </td>
    <td
      :class="`border border-gray-300 text-sm px-3 py-2.5 text-right ${props.bold ? 'font-semibold border-t-2 border-t-gray-400' : ''} ${cellClass(props.payments)}`"
    >
      {{ cellText(props.payments) }}
    </td>
    <td
      :class="`border border-gray-300 text-sm px-3 py-2.5 text-right ${props.bold ? 'font-semibold border-t-2 border-t-gray-400' : ''} ${cellClass(props.viaAdvances)}`"
    >
      {{ cellText(props.viaAdvances) }}
    </td>
    <td
      :class="`border border-gray-300 text-sm px-3 py-2.5 text-right ${props.bold ? 'font-semibold border-t-2 border-t-gray-400' : ''} ${cellClass(props.paid)}`"
    >
      {{ cellText(props.paid) }}
    </td>
    <td
      :class="`border border-gray-300 text-sm px-3 py-2.5 text-right ${props.bold ? 'font-semibold border-t-2 border-t-gray-400' : ''} ${cellClass(props.balance)}`"
    >
      {{ cellText(props.balance) }}
    </td>
  </tr>
</template>
